import { MessageNotif, Trash, VolumeHigh, VolumeSlash } from 'iconsax-react';
import { type MouseEvent, type PropsWithChildren, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOnClickOutside, useToggle } from 'usehooks-ts';
import PinIcon from '@/assets/images/icons/pin.svg?react';
import ReadIcon from '@/assets/images/icons/read.svg?react';
import UnPinIcon from '@/assets/images/icons/unpin.svg?react';
import SwipeableActions, { type SwipeableAction } from '@/components/common/swipeable-actions/swipeable-actions';
import useClearSession from '@/hooks/conversations/use-clear-session';
import useMarkSessionRead from '@/hooks/conversations/use-mark-session-read';
import useUpdateSessionDnd from '@/hooks/sessions/use-update-session-dnd';
import useUpdateSessionTop from '@/hooks/sessions/use-update-session-top';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  session: ConversationSessionsTypeUnion;
}

export default function SessionListItemActions({ className, session, children }: PropsWithChildren<Props>) {
  const { ID, IsTop, UnReadCount, SessionType, IsNotDisturb, LastMsgID } = session;

  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isContextMenuOpened, toggleContextMenuOpen, setContextMenuOpen] = useToggle(false);
  const isDesktop = useIsDesktop();
  const { markAllRead, markUnread } = useMarkSessionRead();
  const { clearSession } = useClearSession(SessionType, ID);
  const { mutateAsync: updateSessionTop } = useUpdateSessionTop(SessionType, ID);
  const { mutateAsync: setDoNotDisturb } = useUpdateSessionDnd(SessionType, ID);

  //context event
  const handleOpenContext = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const x = e.clientX + 10;
    const y = e.clientY - 20;
    const windowHeight = window.innerHeight;
    const isOutsideVisibleArea = y + 160 > windowHeight;
    setPosition({ x, y: isOutsideVisibleArea ? e.clientY - 160 : y });

    toggleContextMenuOpen();
  };
  const handleCloseContext = () => {
    setContextMenuOpen(false);
    setPosition(null);
  };
  useOnClickOutside(wrapperRef, handleCloseContext);
  useEventListener('wheel', handleCloseContext);

  const actions: SwipeableAction[] = [
    {
      key: 'pin',
      placement: 'start',
      icon: IsTop ? <UnPinIcon className='size-5' /> : <PinIcon className='size-5' />,
      label: IsTop ? t('conversation.list.action.unpin') : t('conversation.list.action.pin'),
      onClick: () => updateSessionTop(!IsTop),
      className: 'bg-blue-500 text-white',
    },
    {
      key: 'dnd',
      placement: 'start',
      icon: IsNotDisturb ? (
        <VolumeHigh
          variant='Bold'
          className='size-5'
        />
      ) : (
        <VolumeSlash
          variant='Bold'
          className='size-5'
        />
      ),
      label: IsNotDisturb ? t('conversation.list.action.unmute') : t('conversation.list.action.mute'),
      onClick: () => setDoNotDisturb(!IsNotDisturb),
      className: 'bg-blue-300 text-white',
    },
    {
      key: 'toggle-read',
      placement: 'end',
      icon:
        UnReadCount > 0 ? (
          <ReadIcon className='size-5' />
        ) : (
          <MessageNotif
            variant='Bold'
            className='size-5'
          />
        ),
      label: UnReadCount > 0 ? t('conversation.list.action.read') : t('conversation.list.action.unread'),
      onClick: () =>
        UnReadCount > 0 ? markAllRead(SessionType, ID, LastMsgID, 'session-manual') : markUnread(SessionType, ID, 1),
      className: 'bg-warning text-white',
    },
    {
      key: 'delete',
      placement: 'end',
      icon: (
        <Trash
          variant='Bold'
          className='size-5'
        />
      ),
      label: t('conversation.list.action.delete'),
      onClick: () =>
        makePrompt({
          key: 'hide-session',
          title: t('conversation.purge.confirmation'),
          promise: () => clearSession(),
        }),
      className: 'bg-red-500 text-white',
    },
  ];

  if (isDesktop)
    return (
      <div
        ref={wrapperRef}
        tabIndex={0}
        className={cn('relative w-full', className)}
        onPointerUp={(e) => {
          if (e.button === 0) {
            setContextMenuOpen(false); // if pointer up as primary pointer (e.g. left-click on mouse)
            setPosition(null);
          }
        }}
        onContextMenu={handleOpenContext}>
        <ul
          className={cn(
            'menu flex-col fixed scale-y-0 top-full  justify-between invisible shadow-xl z-10 bg-neutral-0 rounded-lg peer',
            { 'visible scale-y-100': isContextMenuOpened },
          )}
          style={{ left: position?.x, top: position?.y }}>
          {actions.map(({ key, label, onClick }) => (
            <li key={key}>
              <button
                type='button'
                className='flex flex-center'
                onClick={onClick}>
                {label}
              </button>
            </li>
          ))}
        </ul>
        {children}
      </div>
    );

  return (
    <SwipeableActions
      className={cn('items-stretch', className)}
      actions={actions}>
      {children}
    </SwipeableActions>
  );
}
